import { getAttributionBridgeParams } from "@/lib/attribution";
import { getConsentBridgeParam } from "@/lib/consent";

export const DASHBOARD_URL = (
  process.env.NEXT_PUBLIC_DASHBOARD_URL || "https://dashboard.qrhub.io"
).replace(/\/+$/, "");

function applyBridgeParams(url: URL): void {
  const bridge = {
    ...getAttributionBridgeParams(),
    ...getConsentBridgeParam(),
  };
  for (const [key, value] of Object.entries(bridge)) {
    if (value) {
      url.searchParams.set(key, value);
    }
  }
}

/**
 * Builds the dashboard account-create URL, baking in any stored marketing
 * attribution and consent choice.
 *
 * Safe to call from client components only (reads cookie/localStorage).
 */
export function buildDashboardCreateUrl(options?: {
  meno?: boolean;
}): string {
  const url = new URL(`${DASHBOARD_URL}/account/create`);

  if (options?.meno) {
    url.searchParams.set("meno", "true");
  }

  applyBridgeParams(url);
  return url.toString();
}

export function buildDashboardLoginUrl(options?: { meno?: boolean }): string {
  const url = new URL(`${DASHBOARD_URL}/account/login`);
  if (options?.meno) {
    url.searchParams.set("meno", "true");
  }

  // Preferred: carry consent so dashboard account pages do not re-prompt.
  const consent = getConsentBridgeParam();
  for (const [key, value] of Object.entries(consent)) {
    if (value) url.searchParams.set(key, value);
  }

  return url.toString();
}

function getDashboardHostname(): string | null {
  try {
    return new URL(DASHBOARD_URL).hostname;
  } catch {
    return null;
  }
}

/**
 * Resolves signup CTAs that point at the dashboard:
 * - dashboard root → `/account/create` + attribution/consent bridge params
 * - `/account/create` → same path + bridge params (preserves e.g. meno=true)
 * - `/account/login` → append consent only (no attribution)
 * - non-dashboard URLs → unchanged
 *
 * Safe to call from client components only.
 */
export function appendAttributionBridgeParams(href: string): string {
  if (!href || typeof href !== "string") return href;

  try {
    const absolute =
      href.startsWith("http://") || href.startsWith("https://");

    // Relative site paths (e.g. /en/products) are not dashboard CTAs.
    if (!absolute) {
      return href;
    }

    const url = new URL(href);
    const dashboardHost = getDashboardHostname();
    if (!dashboardHost || url.hostname !== dashboardHost) {
      return href;
    }

    const pathname = url.pathname.replace(/\/+$/, "") || "/";

    // Login CTAs: append consent only (preferred, avoids dashboard re-prompt).
    if (pathname === "/account/login") {
      const consent = getConsentBridgeParam();
      for (const [key, value] of Object.entries(consent)) {
        if (value) url.searchParams.set(key, value);
      }
      return url.toString();
    }

    // Signup destinations used across the marketing site: root or /account/create
    if (pathname !== "/" && pathname !== "/account/create") {
      return href;
    }

    const out = new URL(`${DASHBOARD_URL}/account/create`);

    // Preserve existing query params (e.g. meno=true)
    url.searchParams.forEach((value, key) => {
      if (value) {
        out.searchParams.set(key, value);
      }
    });

    applyBridgeParams(out);
    return out.toString();
  } catch {
    return href;
  }
}
